import React, { useRef } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Paper, 
  Typography, 
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAbsences } from '../../contexts/AbsencesContext';
import TechnicianSelect from '../technicians/TechnicianSelect';
import { parse, format, isValid } from 'date-fns';

const ABSENCE_TYPES = [
  { id: 'CP', label: 'CA' },
  { id: 'JTL', label: 'JTL' },
  { id: 'MALADIE', label: 'Arrêt maladie' },
  { id: 'FORMATION', label: 'Formation' },
  { id: 'CM', label: 'CM' },
  { id: 'TPS', label: 'Temps partiel' },
  { id: 'RECUP', label: 'Récupération' },
  { id: 'RECUP_ASTREINTE', label: 'Récup astreinte' },
  { id: 'MEDICAL', label: 'Visite médicale' },
  { id: 'AUTRE', label: 'Autre' }
];

const validationSchema = yup.object({
  technicianId: yup.string().required('Le technicien est requis'),
  startDate: yup.date().required('La date de début est requise'),
  endDate: yup.date()
    .required('La date de fin est requise')
    .min(yup.ref('startDate'), 'La date de fin doit être après la date de début'),
  type: yup.string().required('Le type d\'absence est requis'),
  comment: yup.string(),
  bypassAbsenceValidation: yup.boolean()
});

const AbsenceForm = ({ absence, onClose }) => {
  const { addAbsence, updateAbsence } = useAbsences();
  const endDateRef = useRef(null);
  
  // Convertir les dates de chaînes en objets Date
  const parseDate = (dateValue) => {
    if (!dateValue) return null;
    
    // Si c'est déjà un objet Date, le retourner tel quel
    if (dateValue instanceof Date) return dateValue;
    
    // Si c'est une chaîne, essayer de la parser
    try {
      // Essayer d'abord le format YYYY-MM-DD (format de stockage)
      let date = parse(dateValue, 'yyyy-MM-dd', new Date());
      if (isValid(date)) return date;

      // Si ça ne marche pas, essayer le format DD/MM/YYYY
      date = parse(dateValue, 'dd/MM/yyyy', new Date());
      if (isValid(date)) return date;

      // Si ça ne marche toujours pas, essayer le format DD-MM-YYYY
      date = parse(dateValue, 'dd-MM-yyyy', new Date());
      if (isValid(date)) return date;
    } catch (error) {
      console.error('Erreur lors du parsing de la date:', error);
    }
    return null;
  };

  const formik = useFormik({
    initialValues: {
      technicianId: absence?.technicianId || '',
      startDate: parseDate(absence?.startDate) || null,
      endDate: parseDate(absence?.endDate) || null,
      type: absence?.type || '',
      comment: absence?.comment || '',
      bypassAbsenceValidation: absence?.bypassAbsenceValidation || false
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formattedValues = {
          ...values,
          // Convertir les dates en format YYYY-MM-DD pour le stockage
          startDate: values.startDate ? format(values.startDate, 'yyyy-MM-dd') : null,
          endDate: values.endDate ? format(values.endDate, 'yyyy-MM-dd') : null,
        };

        if (absence?.id) {
          await updateAbsence(absence.id, formattedValues);
        } else {
          await addAbsence(formattedValues);
        }
        onClose();
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement de l\'absence:', error);
      }
    },
  });

  // Gestionnaire pour le changement de date de début
  const handleStartDateChange = (value) => {
    formik.setFieldValue('startDate', value);
    
    // Si on a sélectionné une date de début et qu'il n'y a pas encore de date de fin
    if (value && !formik.values.endDate) {
      // Attendre un court délai pour éviter les conflits visuels
      setTimeout(() => {
        if (endDateRef.current) {
          // Chercher le bouton d'ouverture du calendrier (icône)
          const calendarButton = endDateRef.current.querySelector('button[aria-label*="calendar"], button[aria-label*="calendrier"], .MuiIconButton-root');
          if (calendarButton) {
            calendarButton.click();
          } else {
            // Fallback : essayer de déclencher le focus et clic sur l'input
            const input = endDateRef.current.querySelector('input');
            if (input) {
              input.focus();
              input.click();
            }
          }
        }
      }, 150);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Paper elevation={0} sx={{ p: 3 }}>
        <Box 
          component="form" 
          onSubmit={formik.handleSubmit} 
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3
          }}
        >
          <Typography variant="h6" gutterBottom>
            Informations de l'absence
          </Typography>

          <TechnicianSelect
            value={formik.values.technicianId}
            onChange={(e) => formik.setFieldValue('technicianId', e.target.value)}
            error={formik.touched.technicianId && Boolean(formik.errors.technicianId)}
            helperText={formik.touched.technicianId && formik.errors.technicianId}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Date de début"
                value={formik.values.startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                    helperText={formik.touched.startDate && formik.errors.startDate}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                ref={endDateRef}
                label="Date de fin"
                value={formik.values.endDate}
                onChange={(value) => formik.setFieldValue('endDate', value)}
                minDate={formik.values.startDate}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant="outlined"
                    error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                    helperText={formik.touched.endDate && formik.errors.endDate}
                  />
                )}
              />
            </Grid>
          </Grid>

          <FormControl 
            fullWidth 
            error={formik.touched.type && Boolean(formik.errors.type)}
          >
            <InputLabel id="type-label">Type d'absence</InputLabel>
            <Select
              labelId="type-label"
              id="type"
              name="type"
              value={formik.values.type}
              onChange={formik.handleChange}
              label="Type d'absence"
              MenuProps={{
                sx: {
                  zIndex: 1600
                },
                PaperProps: {
                  sx: {
                    zIndex: 1600
                  }
                }
              }}
            >
              {ABSENCE_TYPES.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.type && formik.errors.type && (
              <FormHelperText>{formik.errors.type}</FormHelperText>
            )}
          </FormControl>

          <TextField
            fullWidth
            id="comment"
            name="comment"
            label="Commentaire (optionnel)"
            value={formik.values.comment}
            onChange={formik.handleChange}
            multiline
            rows={3}
            variant="outlined"
          />

          <FormControlLabel
            control={
              <Checkbox
                id="bypassAbsenceValidation"
                name="bypassAbsenceValidation"
                checked={formik.values.bypassAbsenceValidation}
                onChange={formik.handleChange}
                color="warning"
              />
            }
            label="Bypass validation absence (accord manager) - Permet l'assignation malgré cette absence"
            sx={{ 
              mt: 2,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                color: 'text.secondary'
              }
            }}
          />

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            justifyContent: 'flex-end', 
            mt: 4,
            '& .MuiButton-root': {
              minWidth: 100
            }
          }}>
            <Button 
              onClick={onClose} 
              color="inherit"
              variant="outlined"
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
            >
              {absence ? 'Modifier' : 'Créer'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default AbsenceForm; 